import { FloatingToolbar } from '@latitude/floating-toolbar';
import type {
  FloatingToolbarProps,
  FloatingToolbarStatusConfig,
} from '@latitude/floating-toolbar';
import { Icon } from '@latitude/icons';
import { Spinner } from '@latitude/spinner';
import { Tooltip } from '@latitude/tooltip';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

const withTooltip = (text: string) => (button: ReactNode) => (
  <Tooltip text={text}>{button}</Tooltip>
);

type Props = {
  progressPercentage: number;
  state?: 'saving' | 'saved' | 'unsaved';
  lastSaved?: string;
  canSubmit?: boolean;
  canCommit?: boolean;
  onCommit?: () => void;
  onSubmit?: () => void;
  onValidate?: () => void;
  error?: {
    title: string;
    description: string;
    onLeftClick?: () => void;
    onRightClick?: () => void;
  };
};

const Toolbar = ({
  progressPercentage,
  state,
  lastSaved,
  canCommit,
  canSubmit,
  onCommit,
  onSubmit,
  onValidate,
  error,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [minWidth, setMinWidth] = useState<number | undefined>();
  const isSaving = state === 'saving';
  const isUnsaved = state === 'unsaved';
  const isSubmitDisabled = !canSubmit || isSaving || isUnsaved || !!error;
  const layoutKey = `${!!error}-${isSubmitDisabled}-${!!onCommit}`;
  const prevLayoutKey = useRef(layoutKey);

  useLayoutEffect(() => {
    if (layoutKey !== prevLayoutKey.current) {
      prevLayoutKey.current = layoutKey;
      setMinWidth(undefined);
      return;
    }
    if (wrapperRef.current) {
      const width = wrapperRef.current.offsetWidth;
      setMinWidth((prev) => (!prev || width > prev ? width : prev));
    }
  });

  const isCommitDisabled = !canCommit || isSaving || isUnsaved;
  const isReadyToSubmit = !!canSubmit && !isSaving && !isUnsaved && !error;

  let leading: FloatingToolbarProps['leading'];
  if (error) {
    leading = {
      type: 'pagination',
      props: {
        hasPreviousPage: !!error.onLeftClick,
        hasNextPage: !!error.onRightClick,
        onPrevious: error.onLeftClick ?? (() => undefined),
        onNext: error.onRightClick ?? (() => undefined),
      },
    };
  } else if (!isReadyToSubmit) {
    leading = {
      type: 'progress',
      props: { percentage: progressPercentage, text: true },
    };
  }

  const statuses: FloatingToolbarStatusConfig[] = [];

  if (error) {
    statuses.push({
      status: 'danger',
      label: error.title,
      description: error.description,
      descriptionTooltip: error.description,
      leadingElement: <Icon name="alert-circle" />,
      tooltip: error.title,
      className: 'w-52',
    });
  } else if (isReadyToSubmit) {
    statuses.push({
      status: 'success',
      label: 'Ready to submit',
      leadingElement: <Icon name="check-circle" />,
    });
  }

  if (isSaving) {
    statuses.push({
      label: 'Saving...',
      leadingElement: <Spinner />,
    });
  } else if (isUnsaved) {
    statuses.push({
      label: 'Unsaved changes',
      leadingElement: <Icon name="alert-triangle" />,
      tooltip: "Some changes won't be synced until we're able to reconnect",
    });
  } else if (lastSaved) {
    statuses.push({
      label: `Autosaved ${lastSaved}`,
      leadingElement: <Icon name="upload-cloud" />,
      tooltip:
        'Your progress is automatically saved, so you can return and complete your application later',
    });
  }

  return (
    <div
      ref={wrapperRef}
      className="sticky bottom-0 z-0 mx-auto mt-6 w-fit"
      style={minWidth ? { minWidth } : undefined}
    >
      <FloatingToolbar
        leading={leading}
        statuses={statuses}
        trailingButtonStack={{
          ...(isSubmitDisabled && {
            tertiaryButton: {
              children: 'Check form',
              htmlType: 'button',
              size: 'medium',
              onClick: onValidate,
              wrapper: withTooltip(
                'Check the form for any errors or missing fields before submitting',
              ),
            },
          }),
          ...(onCommit && {
            secondaryButton: {
              children: 'Commit changes',
              htmlType: 'button',
              size: 'medium',
              type: 'outline',
              disabled: isCommitDisabled,
              onClick: onCommit,
              wrapper: withTooltip(
                'All client PII fields must be complete and accurate before committing changes',
              ),
            },
          }),
          primaryButton: {
            children: 'Submit',
            htmlType: 'button',
            size: 'medium',
            disabled: isSubmitDisabled,
            onClick: onSubmit,
            wrapper: withTooltip(
              'Fill in all required fields before submitting your application',
            ),
          },
        }}
      />
    </div>
  );
};

export { Toolbar };
